import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("alp_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-logout on expired/invalid token from protected endpoints.
apiClient.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      const url = (error.config?.url || "").toString();
      const onAuthRoute = url.includes("/auth/login") || url.includes("/auth/register");
      if (!onAuthRoute && localStorage.getItem("alp_token")) {
        localStorage.removeItem("alp_token");
        const path = window.location.pathname;
        // Only bounce if the user is in a protected area, not public pages.
        if (path.startsWith("/app")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export { apiClient };

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await apiClient.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("alp_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, level = "A1") => {
    const { data } = await apiClient.post("/auth/register", { email, password, name, level });
    if (data.access_token) localStorage.setItem("alp_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await apiClient.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("alp_token");
    setUser(false);
  };

  const updateLevel = async (level) => {
    const { data } = await apiClient.patch("/auth/level", { level });
    setUser(data);
    return data;
  };

  const refreshUser = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateLevel, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
